import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getApiBaseUrl } from '../config/api';
import SignupTrendChart from './SignupTrendChart';
import './SignupPulse.css';

const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: 'month', label: 'This month' },
  { id: 'campaign', label: 'Since flyer' }
];

const formatDelta = (delta) => {
  if (delta == null) return '—';
  if (delta === 0) return 'Same as last period';
  const abs = Math.abs(delta);
  return delta > 0 ? `+${abs} vs last period` : `${delta} vs last period`;
};

const SignupPulse = () => {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustom, setShowCustom] = useState(Boolean(searchParams.get('from') || searchParams.get('to')));
  const API_BASE_URL = getApiBaseUrl();

  const rangeQuery = useMemo(() => {
    const from = searchParams.get('from') || '';
    const to = searchParams.get('to') || '';
    if (from || to) return { from, to };
    return { preset: searchParams.get('range') || 'campaign' };
  }, [searchParams]);

  useEffect(() => {
    document.title = 'Portal Sign Ups · AgentBima';
    const robots = document.createElement('meta');
    robots.setAttribute('name', 'robots');
    robots.setAttribute('content', 'noindex, nofollow, noarchive, nosnippet');
    document.head.appendChild(robots);
    const referrer = document.createElement('meta');
    referrer.setAttribute('name', 'referrer');
    referrer.setAttribute('content', 'no-referrer');
    document.head.appendChild(referrer);
    return () => {
      document.title = 'The Monarch Insurance Company';
      robots.remove();
      referrer.remove();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const params = new URLSearchParams();
        if (rangeQuery.from && rangeQuery.to) {
          params.set('from', rangeQuery.from);
          params.set('to', rangeQuery.to);
        } else if (rangeQuery.preset) {
          params.set('preset', rangeQuery.preset);
        }
        const query = params.toString();
        const response = await fetch(`${API_BASE_URL}/api/public/signup-tracker${query ? `?${query}` : ''}`, {
          headers: { 'X-Signup-Tracker-Token': token || '' },
          cache: 'no-store',
          referrerPolicy: 'no-referrer',
          credentials: 'omit'
        });
        const json = await response.json();
        if (!response.ok || !json.success) {
          throw new Error(json.error || 'Unable to load Sign Up tracker');
        }
        if (!cancelled) {
          setData(json);
          setError('');
          setCustomFrom((current) => current || json.range?.from || '');
          setCustomTo((current) => current || json.range?.to || '');
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Unable to load Sign Up tracker');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    setIsLoading(true);
    load();
    const timer = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [API_BASE_URL, token, rangeQuery.from, rangeQuery.to, rangeQuery.preset]);

  const applyPreset = (preset) => {
    setShowCustom(false);
    setSearchParams({ range: preset }, { replace: true });
  };

  const applyCustomRange = (event) => {
    event.preventDefault();
    if (!customFrom || !customTo) return;
    setShowCustom(true);
    setSearchParams({ from: customFrom, to: customTo }, { replace: true });
  };

  const generated = data?.generatedAt
    ? new Date(data.generatedAt).toLocaleString('en-KE', { timeZone: 'Africa/Nairobi' })
    : '';
  const insights = data?.insights || {};
  const activePreset = searchParams.get('from') ? 'custom' : (searchParams.get('range') || 'campaign');
  const delta = insights.previous?.delta;
  const deltaClass = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';

  return (
    <div className="signup-pulse">
      <div className="signup-pulse-shell">
        <header className="signup-pulse-hero">
          <div>
            <p className="signup-pulse-kicker">Monarch Insurance · AgentBima</p>
            <h1>Portal Sign Ups</h1>
            <p className="signup-pulse-sub">
              New agent accounts created through Sign Up. Counts only — no names, phones, or licence numbers.
            </p>
          </div>
          <div className="signup-pulse-live">
            <span className="signup-pulse-dot" aria-hidden="true" />
            Live · Africa/Nairobi
          </div>
        </header>

        <div className="signup-pulse-toolbar" role="tablist" aria-label="Date range">
          {PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              role="tab"
              aria-selected={activePreset === preset.id}
              className={activePreset === preset.id ? 'is-active' : ''}
              onClick={() => applyPreset(preset.id)}
            >
              {preset.label}
            </button>
          ))}
          <button
            type="button"
            role="tab"
            aria-selected={activePreset === 'custom'}
            className={activePreset === 'custom' || showCustom ? 'is-active' : ''}
            onClick={() => setShowCustom(true)}
          >
            Custom
          </button>
        </div>

        {(showCustom || activePreset === 'custom') && (
          <form className="signup-pulse-custom" onSubmit={applyCustomRange}>
            <label>
              From
              <input
                type="date"
                value={customFrom}
                min={data?.minDate || data?.flyerStart || '2026-10-01'}
                max={data?.maxDate || data?.today || undefined}
                onChange={(event) => setCustomFrom(event.target.value)}
              />
            </label>
            <label>
              To
              <input
                type="date"
                value={customTo}
                min={customFrom || data?.minDate || data?.flyerStart || '2026-10-01'}
                max={data?.maxDate || data?.today || undefined}
                onChange={(event) => setCustomTo(event.target.value)}
              />
            </label>
            <button type="submit">Apply dates</button>
          </form>
        )}

        {isLoading && !data && <p className="signup-pulse-status">Loading Sign Ups…</p>}
        {error && <p className="signup-pulse-error">{error}</p>}

        {data && (
          <>
            <p className="signup-pulse-range-label">{data.range?.label || 'Selected period'}</p>
            <div className="signup-pulse-stats">
              <div>
                <span>In this period</span>
                <strong>{data.selectedCount || 0}</strong>
                <em className={deltaClass}>{formatDelta(delta)}</em>
              </div>
              <div>
                <span>Today</span>
                <strong>{data.todayCount || 0}</strong>
                <em>Flyer started {data.flyerStart}</em>
              </div>
              <div>
                <span>Peak day</span>
                <strong>{insights.peakDay?.signups || 0}</strong>
                <em>{insights.peakDay ? insights.peakDay.label : 'No Sign Ups yet'}</em>
              </div>
              <div>
                <span>All Sign Ups</span>
                <strong>{data.allTime || 0}</strong>
                <em>{data.sinceFlyer || 0} since the flyer</em>
              </div>
            </div>
            <div className="signup-pulse-chart-card">
              <SignupTrendChart series={data.series || []} height={340} />
              {(data.selectedCount || 0) === 0 && (
                <p className="signup-pulse-empty">No Sign Ups in this period yet. The chart will move as agents register.</p>
              )}
            </div>
            <p className="signup-pulse-caption">
              Daily new accounts and running total · Updates every minute
              {generated ? ` · Last updated ${generated}` : ''}
              {typeof insights.averageDaily === 'number' ? ` · Average ${insights.averageDaily} a day` : ''}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default SignupPulse;
